import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Filter, ScanLine, Grid3X3, List, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ReceiptCard from '../components/receipts/ReceiptCard';
import ReceiptForm from '../components/receipts/ReceiptForm';
import ReceiptScanner from '../components/receipts/ReceiptScanner';
import { toast } from 'sonner';

const categories = ['all', 'advertising_marketing', 'bank_fees', 'business_meals', 'car_business_use', 'company_car', 'client_gifts', 'continuing_education', 'credit_collection_fees', 'charitable_contributions', 'professional_dues', 'depreciation', 'employee_benefits', 'employee_loans', 'foreign_earned_income', 'insurance', 'raw_materials', 'mortgage_interest', 'licenses_permits', 'maintenance_repairs', 'office_supplies', 'phone_use', 'utilities', 'shipping', 'rent', 'travel', 'software', 'personal_property_use', 'entertainment', 'recruitment_hiring', 'ai_tools_subscriptions', 'other'];

export default function Receipts() {
  const [showForm, setShowForm] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const queryClient = useQueryClient();

  const { data: receipts = [], isLoading } = useQuery({
    queryKey: ['receipts'],
    queryFn: async () => { const { data } = await api.get('/api/receipts?sort=-date&limit=500'); return data; }
  });

  const createMutation = useMutation({
    mutationFn: (data) => api.post('/api/receipts', data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['receipts'] }); setShowForm(false); toast.success('Receipt saved!'); }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api.put(`/api/receipts/${id}`, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['receipts'] }); setShowForm(false); setEditingReceipt(null); toast.success('Receipt updated!'); }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/receipts/${id}`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['receipts'] }); toast.success('Receipt deleted'); }
  });

  const filtered = receipts.filter(r => {
    const matchesSearch = !searchQuery || r.store_name?.toLowerCase().includes(searchQuery.toLowerCase()) || r.notes?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || r.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleSubmit = (formData) => {
    if (editingReceipt?.id) {
      updateMutation.mutate({ id: editingReceipt.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleScanComplete = (data) => {
    setShowScanner(false);
    setEditingReceipt(data);
    setShowForm(true);
  };

  const handleEdit = (receipt) => {
    setEditingReceipt(receipt);
    setShowForm(true);
  };

  const handleDelete = (receipt) => {
    if (window.confirm('Delete this receipt?')) {
      deleteMutation.mutate(receipt.id);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="receipts-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Receipts</h1>
            <p className="text-slate-500 mt-1">{receipts.length} receipts tracked</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => { const token = localStorage.getItem('access_token'); window.open(`${process.env.REACT_APP_BACKEND_URL}/api/export/receipts?token=${token}`, '_blank'); }} data-testid="export-receipts-pdf">
              <Download className="w-4 h-4 mr-2" /> Export PDF
            </Button>
            <Button variant="outline" onClick={() => setShowScanner(true)} data-testid="scan-receipt-button">
              <ScanLine className="w-4 h-4 mr-2" /> Scan
            </Button>
            <Button onClick={() => { setEditingReceipt(null); setShowForm(true); }} className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600" data-testid="add-receipt-manually-button">
              <Plus className="w-4 h-4 mr-2" /> Add Receipt
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search receipts..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="receipt-search-input" />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="receipt-category-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat} className="capitalize">
                    {cat === 'all' ? 'All Categories' : cat.replace(/_/g, ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Receipt List */}
        <div className="space-y-3">
          {filtered.map(receipt => (
            <ReceiptCard
              key={receipt.id}
              receipt={receipt}
              onClick={() => handleEdit(receipt)}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>

        {filtered.length === 0 && !isLoading && (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
            <ScanLine className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No receipts found</h3>
            <p className="text-slate-500 mb-4">Start tracking your expenses by adding a receipt</p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => setShowScanner(true)}>
                <ScanLine className="w-4 h-4 mr-2" /> Scan Receipt
              </Button>
              <Button onClick={() => { setEditingReceipt(null); setShowForm(true); }} className="bg-gradient-to-r from-emerald-500 to-teal-500">
                <Plus className="w-4 h-4 mr-2" /> Add Manually
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showForm && (
          <ReceiptForm receipt={editingReceipt} onSubmit={handleSubmit} onClose={() => { setShowForm(false); setEditingReceipt(null); }} />
        )}
        {showScanner && (
          <ReceiptScanner onScanComplete={handleScanComplete} onClose={() => setShowScanner(false)} />
        )}
      </AnimatePresence>
    </div>
  );
}
